import {
  createContext,
  useContext,
  useId,
  useMemo,
  type ReactNode,
} from "react";

/**
 * The crosshair-sync group for the charts on a screen (design 12.2/12.4: charts on the same screen
 * share a hover position so an operator reading one series sees the same instant on every other).
 *
 * uPlot syncs cursors through a named pub/sub group: every chart created with the same
 * `cursor.sync.key` shares a crosshair. This context hands each chart a stable key for the group it
 * belongs to, so a screen wraps its charts in one `<ChartSyncProvider>` and they sync without any
 * screen-level wiring. Nesting a second provider starts a second, independent group (e.g. a modal's
 * charts should not drive the charts behind it).
 *
 * The key is derived from React's `useId`, so it is unique per provider instance and stable across
 * re-renders — no module-level counter, no collisions between two screens mounted at once. The
 * `Chart` wrapper reads this via `useChartSyncKey`; a chart outside any provider syncs with nothing
 * (its own singleton group keyed by its own id), which is the correct default for a lone chart.
 */

interface ChartSyncValue {
  /** The uPlot sync-group key every chart under this provider shares. */
  readonly syncKey: string;
}

const ChartSyncContext = createContext<ChartSyncValue | null>(null);

export function ChartSyncProvider(props: {
  children: ReactNode;
  /** Override the generated key to join an explicit, caller-named group. Rarely needed. */
  groupKey?: string;
}): ReactNode {
  const generated = useId();
  const value = useMemo<ChartSyncValue>(
    () => ({ syncKey: props.groupKey ?? `chart-sync-${generated}` }),
    [props.groupKey, generated],
  );
  return <ChartSyncContext.Provider value={value}>{props.children}</ChartSyncContext.Provider>;
}

/**
 * The sync-group key for the nearest provider, or `null` when a chart is not inside any provider
 * (a lone chart that should not sync with others). The `Chart` wrapper falls back to its own id so a
 * provider-less chart still gets a valid, isolated group.
 */
export function useChartSyncKey(): string | null {
  const ctx = useContext(ChartSyncContext);
  return ctx === null ? null : ctx.syncKey;
}
